import { useState } from 'react'
import { Search, SlidersHorizontal, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/i18n'
import { Input, Select } from '@/components/ui/Field'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { PRICE_RANGES, SORT_OPTIONS, activeFilterCount } from '@/lib/catalog'
import type { CatalogFilters, Collection, MovementKind, SortKey } from '@/types'

interface Props {
  filters: CatalogFilters
  onChange: (next: CatalogFilters) => void
  onReset: () => void
  collections: Collection[]
  maisons: string[]
  resultCount: number
}

/**
 * Catalogue filters. The same controls serve two layouts: a quiet row on a
 * desktop, and a sheet behind a "Filters" button on a phone — where a row of
 * six selects would eat the screen before a single watch appeared.
 */
export function FilterBar({ filters, onChange, onReset, collections, maisons, resultCount }: Props) {
  const { t, tf } = useI18n()
  const [sheetOpen, setSheetOpen] = useState(false)
  const active = activeFilterCount(filters)

  const set = <K extends keyof CatalogFilters>(key: K, value: CatalogFilters[K]) =>
    onChange({ ...filters, [key]: value })

  const collectionOptions = [
    { value: '', label: t('common.all') },
    ...collections.map((c) => ({ value: c.slug, label: tf(c, 'name') })),
  ]
  const maisonOptions = [{ value: '', label: t('common.all') }, ...maisons.map((m) => ({ value: m, label: m }))]
  const movementOptions = [
    { value: '', label: t('common.all') },
    { value: 'automatic', label: t('movement.automatic') },
    { value: 'manual', label: t('movement.manual') },
    { value: 'quartz', label: t('movement.quartz') },
  ]
  const priceOptions = PRICE_RANGES.map((r) => ({ value: r.value, label: t(r.labelKey) }))
  const sortOptions = SORT_OPTIONS.map((s) => ({ value: s.value, label: t(s.labelKey) }))

  /** The field set itself — rendered inline on desktop, stacked in the sheet. */
  const fields = (
    <>
      <Select
        label={t('catalog.collection')}
        options={collectionOptions}
        value={filters.collection}
        onChange={(e) => set('collection', e.target.value)}
      />
      <Select
        label={t('catalog.maison')}
        options={maisonOptions}
        value={filters.maison}
        onChange={(e) => set('maison', e.target.value)}
      />
      <Select
        label={t('movement.label')}
        options={movementOptions}
        value={filters.movement}
        onChange={(e) => set('movement', e.target.value as '' | MovementKind)}
      />
      <Select
        label={t('catalog.price')}
        options={priceOptions}
        value={filters.priceRange}
        onChange={(e) => set('priceRange', e.target.value)}
      />
    </>
  )

  return (
    <div
      data-upbo-id="catalog-filters"
      data-upbo-type="container"
      data-upbo-label="Catalogue filters"
      className="flex flex-col gap-5"
    >
      {/* ── Search + the two controls that stay visible on every screen ──── */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <Input
            icon={<Search size={15} strokeWidth={1.5} />}
            placeholder={t('catalog.searchPlaceholder')}
            aria-label={t('common.search')}
            value={filters.q}
            onChange={(e) => set('q', e.target.value)}
          />
        </div>

        <div className="hidden lg:block w-[200px]">
          <Select
            options={sortOptions}
            value={filters.sort}
            aria-label={t('catalog.sort')}
            onChange={(e) => set('sort', e.target.value as SortKey)}
          />
        </div>

        <Button
          variant="outline"
          className="lg:hidden"
          icon={<SlidersHorizontal size={15} strokeWidth={1.5} />}
          onClick={() => setSheetOpen(true)}
        >
          {t('common.filters')}
          {active > 0 && <span className="ml-1 text-primary">({active})</span>}
        </Button>
      </div>

      {/* ── Desktop filter row ───────────────────────────────────────────── */}
      <div className="hidden lg:grid grid-cols-4 gap-4">{fields}</div>

      {/* ── Result count + clear ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-4 pt-1">
        <span className="text-[11px] uppercase tracking-[0.16em] text-text-muted">
          {resultCount === 1 ? t('catalog.countOne') : t('catalog.count', { count: resultCount })}
        </span>
        {active > 0 && (
          <button
            type="button"
            onClick={onReset}
            className={cn(
              'inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em]',
              'text-text-muted hover:text-text transition-colors',
            )}
          >
            <X size={13} strokeWidth={1.5} />
            {t('catalog.clearFilters')}
          </button>
        )}
      </div>

      {/* ── Mobile sheet ─────────────────────────────────────────────────── */}
      <Modal
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title={t('common.filters')}
        footer={
          <div className="flex items-center gap-3">
            <Button variant="outline" fullWidth onClick={onReset}>
              {t('common.reset')}
            </Button>
            <Button fullWidth onClick={() => setSheetOpen(false)}>
              {t('common.apply')}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-5">
          {fields}
          <Select
            label={t('catalog.sort')}
            options={sortOptions}
            value={filters.sort}
            onChange={(e) => set('sort', e.target.value as SortKey)}
          />
        </div>
      </Modal>
    </div>
  )
}
